/**
 * useQueryFileValidation Hook
 *
 * Handles YAML file parsing and validation for connector query files
 */

import { useState, useCallback } from 'react';
import yaml from 'js-yaml';
import type { ConnectorType, QueryFile } from '@/types/connectors';
import {
  validateQueryFileStructure,
  formatValidationErrors,
  type ValidationResult,
} from '@/utils/queryFileValidators';

/**
 * Custom YAML tags used by the backend for dynamic date/time placeholders.
 * These tags are resolved at query execution time by the backend.
 * The frontend only needs to accept them during validation.
 */
const CUSTOM_YAML_TAGS = [
  '!last_hour_start',
  '!last_hour_end',
  '!beginningOfYear',
  '!fromPreviousDecember',
  '!yesterday',
  '!68DaysBack',
];

/**
 * Create a custom YAML schema that accepts backend template tags.
 * These tags are placeholder values that get resolved by the backend at runtime.
 */
const createCustomYamlSchema = (): yaml.Schema => {
  const customTypes = CUSTOM_YAML_TAGS.map(
    (tag) =>
      new yaml.Type(tag, {
        kind: 'scalar',
        resolve: () => true,
        construct: (data) => `${tag}${data ? `:${data}` : ''}`, // Preserve tag as string
        represent: (value) => String(value),
      })
  );

  return yaml.DEFAULT_SCHEMA.extend(customTypes);
};

const CUSTOM_YAML_SCHEMA = createCustomYamlSchema();

interface UseQueryFileValidationResult {
  validateFile: (file: File, connectorType: ConnectorType) => Promise<QueryFile>;
  isValidating: boolean;
  validationError: string | null;
}

const MAX_FILE_SIZE = 1024 * 1024; // 1MB
const ALLOWED_EXTENSIONS = ['.yml', '.yaml'];

/**
 * Hook for validating YAML query files
 */
export const useQueryFileValidation = (): UseQueryFileValidationResult => {
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  /**
   * Validate file and return QueryFile object
   */
  const validateFile = useCallback(
    async (file: File, connectorType: ConnectorType): Promise<QueryFile> => {
      setIsValidating(true);
      setValidationError(null);

      try {
        // 1. Check file size
        if (file.size > MAX_FILE_SIZE) {
          throw new Error(`File is too large (max ${MAX_FILE_SIZE / 1024 / 1024}MB)`);
        }

        // 2. Check file extension
        const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
        if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
          throw new Error(`Invalid file type. Only ${ALLOWED_EXTENSIONS.join(', ')} files are allowed`);
        }

        // 3. Read file content
        const content = await readFileAsText(file);

        if (!content || content.trim().length === 0) {
          throw new Error('File is empty');
        }

        // 4. Parse YAML with custom schema to support backend template tags
        let parsedYaml: any;
        try {
          parsedYaml = yaml.load(content, { schema: CUSTOM_YAML_SCHEMA }); // nosemgrep: rules.lgpl.nodejs_scan.javascript-eval-rule-yaml_deserialize  -- js-yaml v4 safe load; schema = DEFAULT_SCHEMA extended with string-only scalar tags (no !!js)
        } catch (parseError: any) {
          // Extract line number from YAML parse error if available
          const lineMatch = parseError.message?.match(/at line (\d+)/);
          const line = lineMatch ? parseInt(lineMatch[1], 10) : undefined;

          throw new Error(
            `Invalid YAML syntax${line ? ` at line ${line}` : ''}: ${parseError.message || 'Unable to parse file'}`
          );
        }

        // 5. Validate structure based on connector type
        const validationResult: ValidationResult = validateQueryFileStructure(parsedYaml, connectorType);

        if (!validationResult.valid) {
          const errorMessages = formatValidationErrors(validationResult.errors);
          throw new Error(`Validation failed:\n${errorMessages.slice(0, 5).join('\n')}`);
        }

        // 6. Return QueryFile object
        const queryFile: QueryFile = {
          filename: file.name,
          content,
          queries: validationResult.queries,
          uploadedAt: new Date(),
        };

        setIsValidating(false);
        return queryFile;
      } catch (error: any) {
        setIsValidating(false);
        setValidationError(error.message || 'File validation failed');
        throw error;
      }
    },
    []
  );

  return {
    validateFile,
    isValidating,
    validationError,
  };
};

/**
 * Helper function to read file as text
 */
const readFileAsText = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const content = event.target?.result;
      if (typeof content === 'string') {
        resolve(content);
      } else {
        reject(new Error('Failed to read file as text'));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsText(file, 'UTF-8');
  });
};
